import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { BehaviorSubject, Subscription, map } from 'rxjs';
import { IconTypes } from 'src/app/standalones/components/pencil-icon/pencil-icon.component';
import { BulkUploadPdfPagePreview } from 'src/app/sessions/session/upload/bulk-upload-panel/bulk-upload-pdf-preview-selector/bulk-upload-pdf-preview-selector.component';
import {
  BulkUploadFileStatus,
  BulkUploadSelectionState,
  BulkUploadFile,
  BulkUploadService,
} from 'src/app/sessions/session/upload/bulk-upload-panel/services/bulk-upload.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

export interface BulkUploadPreviewFile {
  id: string;
  name: string;
  pages: number[];
  pagePreviews: string[];
  pageDimensions: Array<{ width: number; height: number }>;
  selectedPageIndices?: number[];
}

interface BulkUploadPageSelectionFileState {
  id: string;
  name: string;
  pageItems: BulkUploadPdfPagePreview[];
  isCollapsed: boolean;
}

interface BulkUploadPageSelectionViewFile {
  id: string;
  name: string;
  selectedCount: number;
  totalCount: number;
  isCollapsed: boolean;
  pageItems: BulkUploadPdfPagePreview[];
}

interface BulkUploadPageSelectionViewState {
  files: BulkUploadPageSelectionViewFile[];
  selectedCount: number;
  totalSelectableCount: number;
}

@UntilDestroy()
@Component({
  selector: 'app-bulk-upload-page-selection',
  templateUrl: './bulk-upload-page-selection.component.html',
  styleUrls: ['./bulk-upload-page-selection.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BulkUploadPageSelectionComponent implements OnInit, OnDestroy {
  /**
   * Flow overview:
   * - Listen to `BulkUploadService.files$` and derive a PDF-only preview model.
   * - Keep a local `fileStateByIdMap` to preserve selection/collapse between renders.
   * - Build a view model (`viewState$`) that the template consumes with `async`.
   * - Emit `selectionChange` so parent component can update the state accordingly.
   */
  @Output() selectionChange = new EventEmitter<boolean>();

  protected readonly IconTypes = IconTypes;
  // Template view model (file list + selection summary).
  readonly viewState$ = new BehaviorSubject<BulkUploadPageSelectionViewState>({
    files: [],
    selectedCount: 0,
    totalSelectableCount: 0,
  });

  // Error banner uses failed files from the service state.
  readonly failedFileNames$ = this.bulkUploadService.files$.pipe(
    map((files) =>
      files.filter((file) => file.status === BulkUploadFileStatus.Error).map((file) => file.name),
    ),
  );

  private readonly subscriptions = new Subscription();
  // Internal state map so selection survives re-computation.
  private fileStateByIdMap = new Map<string, BulkUploadPageSelectionFileState>();
  // Latest PDF-only preview list derived from service files.
  private previewFiles: BulkUploadPreviewFile[] = [];

  constructor(private readonly bulkUploadService: BulkUploadService) {}

  ngOnInit(): void {
    this.subscriptions.add(
      // Build/refresh preview state whenever files change.
      this.bulkUploadService.files$
        .pipe(untilDestroyed(this))
        .subscribe((files) => this.buildPagesPreviewsFromFiles(files)),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  trackByFileId(
    index: number,
    file: BulkUploadPreviewFile | BulkUploadPageSelectionViewFile,
  ): string {
    return file.id;
  }

  toggleCollapse(fileId: string): void {
    const state = this.fileStateByIdMap.get(fileId);
    if (!state) {
      return;
    }
    // Only mutate collapse state, leave selection intact.
    const nextState = { ...state, isCollapsed: !state.isCollapsed };
    this.fileStateByIdMap.set(fileId, nextState);
    this.updateViewState();
  }

  selectAll(): void {
    this.updateAllSelections(true);
  }

  deselectAll(): void {
    this.updateAllSelections(false);
  }

  handlePageSelectionChange(fileId: string, index: number, isSelected: boolean): void {
    const state = this.fileStateByIdMap.get(fileId);
    if (!state) {
      return;
    }
    // Update only the toggled page for the file.
    this.updateFilePageSelection(fileId, state, index, isSelected);
  }

  getSelectionState(): BulkUploadSelectionState {
    const viewState = this.viewState$.getValue();
    // Used by the panel to commit selections before moving forward.
    return this.buildSelectionState(viewState.files);
  }

  private buildPagesPreviewsFromFiles(files: BulkUploadFile[]) {
    const nextState = new Map<string, BulkUploadPageSelectionFileState>();

    // Derive the PDF-only preview list and merge with existing state.
    this.previewFiles = this.getPreviewFiles(files);
    this.previewFiles.forEach((file) => {
      const existing = this.fileStateByIdMap.get(file.id);
      const previewUrls = this.getPreviewUrls(file);
      const pageItems = this.buildPagePreviewItems(file, previewUrls, existing);
      nextState.set(file.id, {
        id: file.id,
        name: file.name,
        pageItems,
        isCollapsed: existing?.isCollapsed ?? false,
      });
    });

    this.fileStateByIdMap = nextState;
    this.updateViewState();
  }

  private getPreviewUrls(file: BulkUploadPreviewFile): Array<string | undefined> {
    return file.pagePreviews.length > 0
      ? file.pagePreviews
      : Array.from({ length: file.pages.length }).map(() => undefined);
  }

  private buildPagePreviewItems(
    file: BulkUploadPreviewFile,
    previewUrls: Array<string | undefined>,
    existing?: BulkUploadPageSelectionFileState,
  ): BulkUploadPdfPagePreview[] {
    const defaultSelectedIndices = file.selectedPageIndices ?? null;
    return previewUrls.map((imageUrl, index) => ({
      isSelected:
        existing?.pageItems?.[index]?.isSelected ??
        (defaultSelectedIndices ? defaultSelectedIndices.includes(index) : true),
      imageUrl,
      width: file.pageDimensions[index]?.width,
      height: file.pageDimensions[index]?.height,
    }));
  }

  private getPreviewFiles(files: BulkUploadFile[]) {
    return files
      .filter((file) => file.status === BulkUploadFileStatus.Ready && file.isPdf)
      .map((file) => ({
        id: file.id,
        name: file.name,
        pages: file.pages.length ? file.pages : [1],
        pagePreviews: file.pagePreviews ?? [],
        pageDimensions: file.pageDimensions ?? [],
        selectedPageIndices: file.selectedPageIndices,
      }));
  }

  private updateAllSelections(isSelected: boolean): void {
    // Apply to all files and all pages.
    this.fileStateByIdMap.forEach((state, fileId) => {
      const nextItems = state.pageItems.map((item) => ({ ...item, isSelected }));
      this.fileStateByIdMap.set(fileId, { ...state, pageItems: nextItems });
    });
    this.updateViewState();
  }

  private updateFilePageSelection(
    fileId: string,
    state: BulkUploadPageSelectionFileState,
    index: number,
    isSelected: boolean,
  ): void {
    const nextItems = state.pageItems.map((item, itemIndex) =>
      itemIndex === index ? { ...item, isSelected } : item,
    );
    this.fileStateByIdMap.set(fileId, { ...state, pageItems: nextItems });
    this.updateViewState();
  }

  private updateViewState(): void {
    const files = this.getViewFiles();
    const summary = this.getSelectionSummary(files);
    // Single source of truth for the template.
    this.viewState$.next({ files, ...summary });
    this.selectionChange.emit(summary.selectedCount > 0);
  }

  private toViewFile(fileId: string): BulkUploadPageSelectionViewFile | undefined {
    const state = this.fileStateByIdMap.get(fileId);
    if (!state) {
      return undefined;
    }
    const selectedCount = state.pageItems.filter((item) => item.isSelected).length;
    return {
      id: state.id,
      name: state.name,
      selectedCount,
      totalCount: state.pageItems.length,
      isCollapsed: state.isCollapsed,
      pageItems: state.pageItems,
    };
  }

  private getViewFiles(): BulkUploadPageSelectionViewFile[] {
    // Convert the internal state map into template-ready data.
    return this.previewFiles
      .map((file) => this.toViewFile(file.id))
      .filter((file): file is BulkUploadPageSelectionViewFile => !!file);
  }

  private getSelectionSummary(files: BulkUploadPageSelectionViewFile[]) {
    return files.reduce(
      (summary, file) => ({
        selectedCount: summary.selectedCount + file.selectedCount,
        totalSelectableCount: summary.totalSelectableCount + file.totalCount,
      }),
      { selectedCount: 0, totalSelectableCount: 0 },
    );
  }

  private buildSelectionState(files: BulkUploadPageSelectionViewFile[]): BulkUploadSelectionState {
    const selectedFileIds = files.filter((file) => file.selectedCount > 0).map((file) => file.id);
    const selectedPagesIndicesByFileId: Record<string, number[]> = {};

    files.forEach((file) => {
      if (file.selectedCount === 0) {
        return;
      }
      selectedPagesIndicesByFileId[file.id] = file.pageItems
        .map((item, index) => (item.isSelected ? index : -1))
        .filter((index) => index >= 0);
    });

    return { selectedFileIds, selectedPagesIndicesByFileId };
  }
}
